import html2canvas from 'html2canvas';
import { Download, MessageSquare, Twitter } from 'lucide-react';
import { useRef, useState } from 'react';
import { useToast } from '../ui/Toast';
import StatusPill from "../ui/StatusPill";

const ShareTradeCard = ({ trade, onClose }) => {
    const toast = useToast();
    const cardRef = useRef(null);
    const [generating, setGenerating] = useState(false);

    const isWin = trade.netPnl > 0;
    const isLoss = trade.netPnl < 0;
    const pnlColor = isWin ? "text-emerald-400" : isLoss ? "text-red-400" : "text-slate-400";
    const bgGradient = isWin ? "from-emerald-900/40 to-slate-900" : isLoss ? "from-red-900/40 to-slate-900" : "from-slate-800 to-slate-900";

    const handleDownload = async () => {
        if (!cardRef.current) return;
        setGenerating(true);
        try {
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: '#0f172a',
                scale: 3, // High quality
                useCORS: true,
                logging: false,
            });
            
            const image = canvas.toDataURL("image/png");
            const link = document.createElement('a');
            link.href = image;
            link.download = `Trade_${trade.symbol}_${new Date().toISOString().split('T')[0]}.png`;
            link.click();
        } catch (error) {
            console.error("Failed to generate image:", error);
            toast.error("Could not generate image. Please try again.");
        } finally {
            setGenerating(false);
        }
    };

    const handleTwitterShare = () => {
        const text = encodeURIComponent(`Just closed a ${isWin ? 'winning' : 'trade'} on $${trade.symbol}. ${trade.netPnl ? `Secured $${trade.netPnl.toFixed(2)}` : ''}\n\nJournaling my journey on Trading Journal. \n\n#Trading #${trade.symbol}`);
        window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    };

    const handleStockTwitsShare = () => {
        const text = encodeURIComponent(`My latest trade review on $${trade.symbol}. ${isWin ? 'A nice gain!' : 'Learning from this setup.'}`);
        window.open(`https://stocktwits.com/widgets/share?body=${text}`, '_blank');
    };

    return (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.85)", zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'}} onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="max-w-lg w-full flex flex-col gap-4">
                
                {/* Visual Card (The part we render to image) */}
                <div 
                    ref={cardRef}
                    className={`relative p-8 rounded-3xl bg-gradient-to-br ${bgGradient} border border-slate-700/50 shadow-2xl overflow-hidden`}
                >
                    {/* Background Noise/Texture */}
                    <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")' }}></div>

                    {/* Header */}
                    <div className="flex justify-between items-start mb-8 relative z-10">
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <h2 className="text-4xl font-extrabold text-white tracking-tight">{trade.symbol}</h2>
                                <StatusPill type={trade.side === "BUY" ? "success" : "danger"} className="text-xs px-2 py-0.5 border border-slate-700/50">
                                    {trade.side}
                                </StatusPill>
                            </div>
                            <p className="text-slate-400 font-medium tracking-wide text-sm">{trade.securityType || 'EQUITY'} • QTY {trade.quantity}</p>
                        </div>
                        <div className="text-right">
                            <p className={`text-4xl font-black tracking-tighter ${pnlColor}`}>
                                {trade.netPnl > 0 ? '+' : ''}{trade.netPnl ? `$${trade.netPnl.toFixed(2)}` : "Open"}
                            </p>
                            <p className="text-slate-400 text-sm font-medium mt-1">Net P&L</p>
                        </div>
                    </div>

                    {/* Data Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-8 relative z-10">
                        <div className="bg-slate-900/60 rounded-2xl p-4 border border-slate-700/30 backdrop-blur-sm">
                            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">Entry Price</p>
                            <p className="text-slate-200 text-xl font-bold">${trade.entryPrice?.toFixed(2) || "N/A"}</p>
                        </div>
                        <div className="bg-slate-900/60 rounded-2xl p-4 border border-slate-700/30 backdrop-blur-sm">
                            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">Exit Price</p>
                            <p className="text-slate-200 text-xl font-bold">${trade.exitPrice?.toFixed(2) || "N/A"}</p>
                        </div>
                        <div className="col-span-2 bg-slate-900/60 rounded-2xl p-4 border border-slate-700/30 backdrop-blur-sm">
                            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">Strategy</p>
                            <p className="text-slate-200 text-md font-medium">{trade.strategy || "Discretionary"}</p>
                        </div>
                    </div>

                    {/* Footer / Watermark */}
                    <div className="flex justify-between items-end border-t border-slate-700/50 pt-6 relative z-10">
                        <div>
                            <p className="text-slate-300 font-bold text-lg tracking-tight flex items-center gap-2">
                                <span className="text-emerald-500">📈</span> Trading Journal
                            </p>
                            <p className="text-slate-500 text-xs mt-1">Verified Trade Record</p>
                        </div>
                        <div className="text-right">
                            <p className="text-slate-500 text-xs font-mono">{trade.exitTime ? new Date(trade.exitTime).toLocaleDateString() : new Date().toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>

                {/* Actions Layer */}
                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-4 flex justify-between items-center shadow-lg">
                    <button 
                        onClick={handleDownload} 
                        disabled={generating}
                        className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
                    >
                        {generating ? <span className="animate-pulse">Generating...</span> : <><Download size={18} /> Download Card</>}
                    </button>
                    
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={handleTwitterShare}
                            className="p-3 bg-[#1DA1F2]/10 text-[#1DA1F2] hover:bg-[#1DA1F2]/20 rounded-xl transition-colors"
                            title="Share on Twitter / X"
                        >
                            <Twitter size={20} />
                        </button>
                        <button 
                            onClick={handleStockTwitsShare}
                            className="p-3 bg-slate-700 text-slate-300 hover:bg-slate-600 rounded-xl transition-colors flex items-center gap-2 font-medium"
                            title="Share on StockTwits"
                        >
                            <MessageSquare size={20} />
                        </button>
                        <button 
                            onClick={onClose}
                            className="ml-2 p-3 text-slate-400 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareTradeCard;
